import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { FiCamera, FiUpload, FiX, FiCheckCircle, FiDollarSign, FiUser, FiInfo, FiAlertCircle } from 'react-icons/fi';
import supabaseService from '../services/supabaseService';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../utils/helpers';

export default function QrScannerModal({ isOpen, onClose, clientId, clientWallet, onPurchaseSuccess, onWalletCodeDetected }) {
  const [scanState, setScanState] = useState('scanning'); // scanning | loading_details | details | purchasing | success
  const [errorMsg, setErrorMsg] = useState(null);
  const [scannedService, setScannedService] = useState(null);
  const [scannedProvider, setScannedProvider] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);

  const html5QrCodeRef = useRef(null);
  const fileInputRef = useRef(null);
  const scannerId = 'qr-scanner-element';

  // تنظيف الكاميرا وإيقاف التشغيل
  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        console.error('Failed to stop scanner:', err);
      }
    }
    setCameraActive(false);
  };

  // تهيئة الكاميرا والمسح
  const startScanner = async () => {
    try {
      setErrorMsg(null);
      setScanState('scanning');
      setScannedService(null);
      setScannedProvider(null);

      // التأكد من وجود العنصر في الـ DOM قبل البدء
      setTimeout(async () => {
        try {
          if (!document.getElementById(scannerId)) return;

          const html5QrCode = new Html5Qrcode(scannerId);
          html5QrCodeRef.current = html5QrCode;

          const config = { fps: 10, qrbox: { width: 250, height: 250 } };

          await html5QrCode.start(
            { facingMode: 'environment' },
            config,
            (decodedText) => {
              handleQrDecoded(decodedText);
            },
            (errorMessage) => {
              // خطأ تتبع عادي (عدم وجود كود في الإطار) - نتجاهله
            }
          );
          setCameraActive(true);
        } catch (err) {
          console.error('Camera initialization error:', err);
          setErrorMsg('تعذر تشغيل الكاميرا. يرجى التأكد من منح صلاحية الوصول للكاميرا.');
          setCameraActive(false);
        }
      }, 300);
    } catch (err) {
      console.error(err);
      setErrorMsg('تعذر تشغيل الكاميرا.');
    }
  };

  // معالجة فك ترميز الكود بنجاح
  const handleQrDecoded = async (text) => {
    // إيقاف الكاميرا فوراً
    await stopScanner();

    // ✅ 1. التحقق من كود المحفظة الشخصي (تحويل أموال) — فورمات جديد بـ prefix
    if (text.startsWith('khidma_wallet:')) {
      const walletCode = text.split(':')[1];
      if (!walletCode) {
        setErrorMsg('رمز المحفظة غير صحيح.');
        setScanState('scanning');
        setTimeout(startScanner, 2000);
        return;
      }
      // إغلاق الماسح الضوئي واستدعاء callback التنقل
      if (onWalletCodeDetected) {
        onWalletCodeDetected(walletCode);
        onClose();
      } else {
        // إذا لم يكن هناك callback، نفتح الرابط مباشرة
        window.location.href = `/qr-transfer/${walletCode}`;
      }
      return;
    }

    // ✅ 1.5. توافقية مع أكواد محفظة قديمة بلا prefix (فورمات خام)
    if (!text.startsWith('khidma_purchase:') && /^[A-Za-z0-9]{4,12}$/.test(text.trim())) {
      const walletCode = text.trim().toUpperCase();
      if (onWalletCodeDetected) {
        onWalletCodeDetected(walletCode);
        onClose();
      } else {
        window.location.href = `/qr-transfer/${walletCode}`;
      }
      return;
    }

    // ✅ 2. التحقق من كود شراء منتج (المنطق الحالي)
    if (!text.startsWith('khidma_purchase:')) {
      setErrorMsg('رمز QR هذا غير صالح لمشتريات تطبيق خدمة.');
      setScanState('scanning');
      // إعادة التشغيل بعد ثانيتين
      setTimeout(startScanner, 2000);
      return;
    }

    const serviceId = text.split(':')[1];
    if (!serviceId) {
      setErrorMsg('رمز منتج غير صحيح.');
      setScanState('scanning');
      setTimeout(startScanner, 2000);
      return;
    }

    // جلب التفاصيل
    setScanState('loading_details');
    try {
      const details = await supabaseService.getServiceDetails(serviceId);
      if (!details || !details.service) {
        setErrorMsg('المنتج أو الخدمة غير متوفرة أو تم حذفها.');
        setScanState('scanning');
        setTimeout(startScanner, 2000);
        return;
      }
      setScannedService(details.service);
      setScannedProvider(details.provider);
      setScanState('details');
    } catch (err) {
      console.error(err);
      setErrorMsg('حدث خطأ أثناء جلب تفاصيل الخدمة.');
      setScanState('scanning');
      setTimeout(startScanner, 2000);
    }
  };

  // معالجة رفع صورة وفك تشفيرها
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    await stopScanner();
    setScanState('loading_details');
    setErrorMsg(null);

    try {
      const html5QrCode = new Html5Qrcode(scannerId);
      html5QrCodeRef.current = html5QrCode;
      
      const decodedText = await html5QrCode.scanFile(file, true);
      handleQrDecoded(decodedText);
    } catch (err) {
      console.error('File scan error:', err);
      setErrorMsg('فشل فك تشفير كود QR من الصورة المرفوعة. يرجى رفع صورة واضحة.');
      setScanState('scanning');
      // إعادة تشغيل الكاميرا
      startScanner();
    }
  };

  // تنفيذ عملية الشراء الفعلية
  const handlePurchase = async () => {
    if (!scannedService) return;

    // تحقق محلي سريع
    if (scannedService.stock !== null && scannedService.stock <= 0) {
      toast.error('عذراً، نفد مخزون هذا المنتج.');
      return;
    }

    if (clientWallet < scannedService.price) {
      toast.error('رصيد محفظتك غير كافٍ لإتمام عملية الشراء.');
      return;
    }

    setScanState('purchasing');
    try {
      const result = await supabaseService.purchaseViaQr(clientId, scannedService.id);
      
      if (result && result.success) {
        setScanState('success');
        toast.success('تمت عملية الشراء بنجاح!');
        if (onPurchaseSuccess) {
          onPurchaseSuccess();
        }
      } else {
        const msg = result?.message || 'insufficient_balance';
        let friendlyMsg = 'فشلت عملية الشراء.';
        if (msg === 'insufficient_balance') friendlyMsg = 'رصيد المحفظة غير كافٍ.';
        else if (msg === 'out_of_stock') friendlyMsg = 'نفد مخزون المنتج.';
        else if (msg === 'service_not_found') friendlyMsg = 'المنتج غير موجود.';
        
        setErrorMsg(friendlyMsg);
        setScanState('details');
        toast.error(friendlyMsg);
      }
    } catch (err) {
      console.error(err);
      const friendlyMsg = err.message?.includes('balance') ? 'رصيدك غير كافٍ' : 'فشلت عملية الشراء. حاول مرة أخرى.';
      setErrorMsg(friendlyMsg);
      setScanState('details');
      toast.error(friendlyMsg);
    }
  };

  // تأثير فتح وإغلاق الشاشة
  useEffect(() => {
    if (isOpen) {
      startScanner();
    } else {
      stopScanner();
    }
    return () => {
      stopScanner();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in" dir="rtl">
      <div className="bg-white w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl relative flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <span className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
              <FiCamera className="w-5 h-5" />
            </span>
            شراء سريع بـ QR Code
          </h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-xl transition">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 flex flex-col items-center justify-center">
          
          {/* 1. SCANNING STATE */}
          {scanState === 'scanning' && (
            <div className="w-full flex flex-col items-center gap-5">
              <div className="relative w-full max-w-[280px] aspect-square rounded-2xl overflow-hidden border-2 border-dashed border-blue-500 bg-gray-900 shadow-inner flex items-center justify-center">
                
                {/* عنصر المسح */}
                <div id={scannerId} className="absolute inset-0 w-full h-full object-cover" />
                
                {!cameraActive && !errorMsg && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white/50 p-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
                    <span className="text-xs">جاري تشغيل الكاميرا...</span>
                  </div>
                )}

                {errorMsg && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-950/90 text-red-200 p-4 text-center">
                    <FiAlertCircle className="text-3xl mb-2" />
                    <span className="text-xs leading-relaxed">{errorMsg}</span>
                  </div>
                )}
                
                {cameraActive && (
                  <div className="absolute top-2 right-2 bg-green-500/80 backdrop-blur text-white text-[10px] px-2 py-0.5 rounded-full font-bold animate-pulse">
                    الكاميرا نشطة
                  </div>
                )}
              </div>

              <div className="text-center max-w-sm">
                <p className="text-sm text-gray-600 font-bold mb-1">وجه الكاميرا نحو كود QR للمنتج</p>
                <p className="text-xs text-gray-400">سيتم التعرف على الكود تلقائياً وفتح شاشة الشراء السريع</p>
              </div>

              <div className="w-full max-w-[280px] flex flex-col gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 py-3 px-4 rounded-xl text-xs font-bold transition active:scale-95 shadow-sm"
                >
                  <FiUpload className="w-4 h-4" />
                  رفع صورة الكود من الجهاز
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {/* 2. LOADING STATE */}
          {scanState === 'loading_details' && (
            <div className="flex flex-col items-center py-10 gap-3">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-sm font-bold text-gray-700">جاري قراءة الكود وجلب معلومات المنتج...</p>
            </div>
          )}

          {/* 3. DETAILS STATE */}
          {scanState === 'details' && scannedService && (
            <div className="w-full flex flex-col gap-5">
              
              {/* بطاقة تفاصيل الخدمة */}
              <div className="bg-gray-50 rounded-2xl border border-gray-100 p-4 flex gap-4 items-start">
                {scannedService.imageUrl ? (
                  <img src={scannedService.imageUrl} alt={scannedService.name} className="w-24 h-24 rounded-xl object-cover border border-gray-200 flex-shrink-0" />
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0 text-xl font-bold">📦</div>
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full inline-block mb-1.5">
                    {scannedService.productType ? 'منتج رقمي' : 'منتج / خدمة'}
                  </span>
                  <h4 className="text-base font-bold text-gray-900 truncate mb-1">{scannedService.name}</h4>
                  <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-1">{scannedService.description || 'لا يوجد وصف للمنتج.'}</p>
                  
                  {/* المزود */}
                  {scannedProvider && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <img src={scannedProvider.avatarUrl || 'https://via.placeholder.com/150'} alt="Avatar" className="w-5 h-5 rounded-full object-cover" />
                      <span className="text-xs font-medium text-gray-700">{scannedProvider.fullName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* تفاصيل السعر والتحذيرات */}
              <div className="space-y-3.5">
                <div className="flex justify-between items-center bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50">
                  <div className="flex items-center gap-2 text-blue-900 font-bold text-sm">
                    <FiDollarSign className="w-4 h-4 text-blue-600" />
                    المبلغ الإجمالي للمنتج
                  </div>
                  <span className="text-lg font-extrabold text-blue-600">
                    {scannedService.price?.toLocaleString('ar-DZ')} د.ج
                  </span>
                </div>

                <div className="bg-gray-50 p-4 rounded-2xl space-y-2 border border-gray-100 text-xs">
                  <div className="flex justify-between text-gray-600">
                    <span>رصيد محفظتك الحالي:</span>
                    <span className="font-bold">{formatCurrency(clientWallet)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>الرصيد بعد الشراء:</span>
                    <span className={`font-bold ${clientWallet >= scannedService.price ? 'text-green-600' : 'text-red-500'}`}>
                      {formatCurrency(clientWallet - scannedService.price)}
                    </span>
                  </div>
                  {scannedService.stock !== null && (
                    <div className="flex justify-between text-gray-600">
                      <span>المخزون المتوفر:</span>
                      <span className={`font-bold ${scannedService.stock > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {scannedService.stock > 0 ? `${scannedService.stock} وحدات` : 'نفذ من المخزن'}
                      </span>
                    </div>
                  )}
                </div>

                {errorMsg && (
                  <div className="bg-red-50 text-red-600 text-xs p-3.5 rounded-xl flex items-start gap-2 border border-red-100">
                    <FiAlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{errorMsg}</span>
                  </div>
                )}
              </div>

              {/* أزرار الإجراءات */}
              <div className="flex gap-3 mt-2 flex-shrink-0">
                <button
                  onClick={handlePurchase}
                  disabled={clientWallet < scannedService.price || (scannedService.stock !== null && scannedService.stock <= 0)}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-300 text-white font-bold py-3.5 px-4 rounded-2xl text-sm transition-all active:scale-95 shadow-lg shadow-blue-500/10 flex items-center justify-center gap-1.5"
                >
                  <FiCheckCircle className="w-4.5 h-4.5" />
                  شراء الآن من المحفظة
                </button>
                <button
                  onClick={startScanner}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3.5 px-6 rounded-2xl text-sm transition active:scale-95 border border-gray-200"
                >
                  إعادة مسح
                </button>
              </div>
            </div>
          )}

          {/* 4. PURCHASING STATE */}
          {scanState === 'purchasing' && (
            <div className="flex flex-col items-center py-10 gap-3">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-sm font-bold text-gray-700">جاري إتمام المعاملة الآمنة وخصم الرصيد...</p>
            </div>
          )}

          {/* 5. SUCCESS STATE */}
          {scanState === 'success' && (
            <div className="flex flex-col items-center py-8 gap-4 text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-3xl shadow-sm animate-bounce">✓</div>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">تمت العملية بنجاح!</h4>
                <p className="text-xs text-gray-500 leading-relaxed max-w-xs">
                  تم خصم {scannedService?.price?.toLocaleString('ar-DZ')} د.ج من محفظتك وتوثيق طلبك كمكتمل. يمكنك متابعة طلبك في التبويب الخاص بالطلبات.
                </p>
              </div>
              <button
                onClick={onClose}
                className="mt-4 bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 px-8 rounded-xl text-xs transition active:scale-95 shadow-md"
              >
                حسناً، إغلاق
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}